// FreezenSlush custom theme — base JS (vanilla, no deps)
(function () {
  'use strict';

  const routes = window.themeRoutes || {};

  /* ---------- Bundle discount routing ----------
     Shopify cannot express a 10/15/20 volume ladder with AUTOMATIC discounts: minimum quantity
     has no upper bound, so three automatic discounts would overlap and either misfire or compound
     (a 4-cup order taking 10% AND 15% AND 20%). Instead there are three CODE discounts, each
     carrying its own minimum-quantity requirement (BUNDLE10 >=2, BUNDLE15 >=3, BUNDLE20 >=4).
     This picks the best tier the cart currently qualifies for and routes checkout through
     Shopify's /discount/<CODE> link, which applies the code then forwards to checkout.

     Exposing the code in a URL is not exploitable: the minimum-quantity rule lives on the
     discount itself, so pasting BUNDLE20 with one cup does nothing. Every route to checkout goes
     through here, and the dynamic checkout button was removed earlier, so nothing bypasses it. */
  const FS_BUNDLE_HANDLE = 'slushycup';
  const FS_BUNDLE_TIERS = [
    { min: 4, code: 'BUNDLE20' },
    { min: 3, code: 'BUNDLE15' },
    { min: 2, code: 'BUNDLE10' },
  ];

  function fsBundleUrlFor(cart) {
    if (fsCartHasForeignDiscount(cart)) return '/checkout';
    let qty = 0;
    ((cart && cart.items) || []).forEach((i) => {
      if (i && i.handle === FS_BUNDLE_HANDLE) qty += i.quantity || 0;
    });
    const tier = FS_BUNDLE_TIERS.find((t) => qty >= t.min);
    return tier ? '/discount/' + tier.code + '?redirect=%2Fcheckout' : '/checkout';
  }

  /* A customer-entered promo code lives on the Shopify session, and routing through
     /discount/BUNDLExx would replace it. The drawer's promo form sets this flag, so the bundle
     router stands down and lets their code win rather than silently swapping it. */
  function fsManualCodeUsed() {
    try { if (sessionStorage.getItem('fsManualDiscount') === '1') return true; } catch (e) {}
    return false;
  }
  /* The drawer's promo form used to be the ONLY thing that set the flag above, and it has been
     removed. So also treat "the cart already carries a discount that is not one of ours" as a
     customer code we must not clobber — this covers people who arrived on a /discount/ link from
     an email, who the sessionStorage flag never covered in the first place. */
  function fsCartHasForeignDiscount(cart) {
    /* Reads `discount_codes` and the LINE-LEVEL `items[].discounts`, NOT `discount_applications`.
       Verified on a real cart 2026-08-19: with BUNDLE20 applied, `discount_applications` is not even
       a top-level key and `cart_level_discount_applications` is [] — the discount is allocated per
       line. The first version of this function read those two fields and therefore ALWAYS returned
       false, i.e. it protected nothing. */
    if (!cart) return false;
    const ours = FS_BUNDLE_TIERS.map((t) => t.code);
    const codes = []
      .concat((cart.discount_codes || []).map((d) => (d && (d.code || d)) || ''))
      .concat((cart.items || []).flatMap((i) => (i.discounts || []).map((d) => (d && d.title) || '')));
    return codes
      .map((c) => String(c).toUpperCase())
      .filter(Boolean)
      .some((c) => ours.indexOf(c) === -1);
  }
  document.addEventListener('submit', (e) => {
    const f = e.target.closest && e.target.closest('[data-cd-promo]');
    if (!f) return;
    const code = (f.querySelector('input[name="code"]').value || '').trim();
    if (!code) { e.preventDefault(); return; }
    e.preventDefault();
    try { sessionStorage.setItem('fsManualDiscount', '1'); } catch (err) {}
    window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=%2Fcart';
  });

  /* Show the bundle discount IN the cart, not just at checkout.
     Shopify has no AJAX "apply discount" endpoint — /discount/<CODE> is the only mechanism, and it
     works by setting the discount on the session. Fetching it same-origin sets that cookie without
     navigating, after which /cart.js reports real line-level discounts and the drawer renders a
     genuine Discount row. This is NOT a cosmetic calculation: the number shown is Shopify's own.
     Guards: never overrides a code the customer typed, and never re-applies the same tier twice. */
  let fsAppliedTier = null;
  async function fsAutoApplyBundle(cart) {
    if (fsManualCodeUsed() || fsCartHasForeignDiscount(cart)) return false;
    let qty = 0;
    ((cart && cart.items) || []).forEach((i) => {
      if (i && i.handle === FS_BUNDLE_HANDLE) qty += i.quantity || 0;
    });
    const tier = FS_BUNDLE_TIERS.find((t) => qty >= t.min);
    const code = tier ? tier.code : null;
    if (!code || code === fsAppliedTier) return false;
    try {
      await fetch('/discount/' + code + '?redirect=%2Fcart.js', { credentials: 'same-origin' });
      fsAppliedTier = code;
      return true;
    } catch (e) {
      return false;   // fail open: checkout routing still applies it
    }
  }

  function fsGoToCheckout() {
    // Fail open: if the cart cannot be read, still send them to checkout rather than stall.
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((c) => { window.location.href = fsManualCodeUsed() ? '/checkout' : fsBundleUrlFor(c); })
      .catch(() => { window.location.href = '/checkout'; });
  }

  /* The mix & match multipack block further down this file is its own IIFE, so it cannot see
     this closure. Publish the helper so that path routes through the discount too. */
  window.fsGoToCheckout = fsGoToCheckout;

  /* Catch every remaining checkout entry point: the cart page and cart drawer submit buttons,
     and the drawer's rendered checkout link. */
  document.addEventListener('click', (e) => {
    const el = e.target.closest && e.target.closest('[name="checkout"], [data-bundle-checkout]');
    if (!el) return;
    e.preventDefault();
    fsGoToCheckout();
  }, true);

  // ---------- Announcement bar ----------
  document.querySelectorAll('.announcement').forEach((el) => {
    const msgs = el.querySelectorAll('.announcement__msg');
    const interval = parseInt(el.dataset.interval || '5000', 10);
    let i = 0;
    if (msgs.length > 1) {
      setInterval(() => {
        msgs[i].classList.remove('is-active');
        i = (i + 1) % msgs.length;
        msgs[i].classList.add('is-active');
      }, interval);
    }
  });

  // ---------- Header sticky shadow ----------
  const header = document.querySelector('.site-header');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // ---------- Drawers (menu, cart, search) ----------
  // A closed drawer is visibility:hidden AND inert, so nothing inside it can be
  // tabbed to or read out. `inert` is dropped on open. The markup used to carry a
  // STATIC aria-hidden="true" that was never removed, which left the menu and search
  // drawers muted to screen readers even while open — do not put it back.
  function bindDrawer(drawer) {
    if (!drawer) return;
    const panel = drawer.querySelector('.drawer__panel');
    const scrim = drawer.querySelector('.drawer__scrim');
    if (panel) panel.tabIndex = -1;
    let opener = null;
    // Recomputed on every call: base.js replaces the cart drawer's body wholesale.
    const focusables = () =>
      Array.from(
        (panel || drawer).querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null);
    drawer.inert = true;
    const open = (from) => {
      opener = from || document.activeElement;
      drawer.inert = false;
      drawer.setAttribute('open', '');
      document.body.style.overflow = 'hidden';
      const first = focusables()[0];
      (first || panel || drawer).focus({ preventScroll: true });
      drawer.dispatchEvent(new CustomEvent('drawer:open'));
    };
    const close = () => {
      // Focus has to leave BEFORE the drawer goes inert, or the browser drops it on <body>
      // and the keyboard user loses their place on the page.
      if (drawer.contains(document.activeElement)) {
        const back = opener && document.contains(opener) ? opener : null;
        if (back) back.focus({ preventScroll: true });
        else document.activeElement.blur();
      }
      drawer.inert = true;
      drawer.removeAttribute('open');
      document.body.style.overflow = '';
      drawer.dispatchEvent(new CustomEvent('drawer:close'));
    };
    scrim && scrim.addEventListener('click', close);
    /* Delegated, NOT a one-time NodeList. The cart drawer's body and footer are replaced
       wholesale on every refresh, and the empty state's "Pick your cups" button lives inside
       that body — bound once at load, it would go dead the first time the bag emptied. */
    drawer.addEventListener('click', (e) => {
      if (e.target.closest('[data-drawer-close]')) close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.hasAttribute('open')) close();
    });
    drawer.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab' || !drawer.hasAttribute('open')) return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
    drawer._open = open;
    drawer._close = close;
  }
  const menuDrawer = document.querySelector('[data-drawer="menu"]');
  const cartDrawer = document.querySelector('[data-drawer="cart"]');
  const searchDrawer = document.querySelector('[data-drawer="search"]');
  bindDrawer(menuDrawer);
  bindDrawer(cartDrawer);
  bindDrawer(searchDrawer);
  document.querySelectorAll('[data-open-drawer]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const target = document.querySelector(`[data-drawer="${btn.dataset.openDrawer}"]`);
      // Pass the button explicitly: macOS Safari and Firefox do not focus a <button>
      // on click, so document.activeElement would be <body> and focus would have
      // nowhere to return to when the drawer closes.
      target && target._open && target._open(btn);
    });
  });

  // ---------- Cart ----------
  const money = (cents) => {
    const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.themeCurrency || 'USD',
    });
    return formatter.format((cents || 0) / 100);
  };

  async function fetchCart() {
    const res = await fetch(routes.cart || '/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  async function addToCart(items) {
    const res = await fetch((routes.cart_add || '/cart/add') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: Array.isArray(items) ? items : [items] }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || 'Could not add to cart');
    }
    return res.json();
  }

  /* /cart/change.js accepts EITHER `line` (1-based index) or `id` (the line item key).
     changeCart() below sends `line`, so passing a key to it silently does nothing — that is
     exactly what made the cart page ignore every quantity click. This one sends `id`. */
  async function changeCartByKey(key, quantity) {
    const res = await fetch((routes.cart_change || '/cart/change') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    return res.json();
  }

  async function changeCart(line, quantity) {
    const res = await fetch((routes.cart_change || '/cart/change') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    return res.json();
  }

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count < 1;
    });
  }

  /* The drawer's contents are rendered by snippets/cart-drawer.liquid and fetched from
     /cart?view=drawer (templates/cart.drawer.liquid). This function used to REBUILD them in
     JS with its own markup, which meant the rich drawer was thrown away the moment anything
     was added: no quantity steppers, no payment icons, no assurance row, no discount lines.
     It also emitted a plain <a href="/checkout">, which does not match the capture-phase
     selector above that routes checkout through the bundle discount — so any customer who
     added a second cup silently lost their 10-20%. One source of truth now: the Liquid. */
  /* routes.cart is the AJAX endpoint '/cart.js', NOT the cart page — the page is
     routes.cart_url. Using the wrong one silently fetches JSON and drops the drawer to its
     fallback on every single update. */
  const CART_PAGE_URL = routes.cart_url || '/cart';
  const CART_DRAWER_URL = CART_PAGE_URL + '?view=drawer';

  async function renderCartDrawer(cart) {
    const body = document.querySelector('[data-cart-drawer-body]');
    const footer = document.querySelector('[data-cart-drawer-footer]');
    if (!body || !footer) return;
    /* Apply the tier BEFORE fetching the markup, so the very first render already shows it. */
    if (cart && cart.items) { try { await fsAutoApplyBundle(cart); } catch (e) {} }
    try {
      const res = await fetch(CART_DRAWER_URL, { headers: { Accept: 'text/html' }, cache: 'no-store' });
      if (!res.ok) throw new Error('drawer ' + res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const nextBody = doc.querySelector('[data-cart-drawer-body]');
      const nextFoot = doc.querySelector('[data-cart-drawer-footer]');
      if (!nextBody || !nextFoot) throw new Error('drawer markup missing');
      body.innerHTML = nextBody.innerHTML;
      footer.innerHTML = nextFoot.innerHTML;
    } catch (err) {
      /* Fail to the cart PAGE, never to a home-made checkout button — a hand-built fallback is
         exactly how the discount bypass got in. The full cart works and routes correctly. */
      body.innerHTML =
        '<p class="cd-fallback">We could not refresh your bag just now. ' +
        '<a href="' + CART_PAGE_URL + '">Open the full bag</a> to carry on.</p>';
      footer.innerHTML = '';
    }
    if (cart && typeof cart.item_count === 'number') updateCartCount(cart.item_count);
  }

  window.FS = {
    fetchCart, addToCart, changeCart, updateCartCount, renderCartDrawer, money,
    openCart: async () => {
      const c = await fetchCart();
      updateCartCount(c.item_count);
      await renderCartDrawer(c);
      cartDrawer && cartDrawer._open && cartDrawer._open();
    },
  };

  /* Quantity and remove used to be plain links to /cart/change, so every ± click reloaded the
     whole page and dropped the customer back at the top of it. The hrefs are kept as the no-JS
     fallback; with JS they are intercepted here.
     cartBusy is not cosmetic: line indices SHIFT when a line is removed, so a second click
     landing before the re-render would change the wrong line. */
  /* ---------- optimistic quantity stepper (cart drawer) ----------
     Every tap used to block on THREE sequential round trips before the number on screen moved:
     POST /cart/change.js, then a /discount/<CODE> hit when the bundle tier shifts, then a full
     HTML fetch of /cart?view=drawer. A `cartBusy` flag swallowed further taps for the whole
     duration, so holding + felt broken and the totals trailed the clicking.
     Now the number, the minus button's disabled state and the cart count repaint IMMEDIATELY,
     and the server sync is debounced to the LAST value per line — five taps send one request
     instead of five queued ones. The re-render still replaces the body with server-rendered
     markup, so the displayed truth is still Shopify's, never a number we invented.
     Keyed on the LINE ITEM KEY, not the 1-based index, so a removal mid-flight cannot retarget
     the wrong line. */
  const qtyTarget = new Map();
  const qtyTimer = new Map();

  function drawerQtySum() {
    return Array.prototype.reduce.call(
      document.querySelectorAll('[data-cart-drawer-body] .cd-qn'),
      (n, el) => n + (parseInt(el.textContent, 10) || 0), 0);
  }

  function stepLine(btn, key, dir) {
    const ctrl = btn.closest('.cd-qty-ctrl');
    const numEl = ctrl && ctrl.querySelector('.cd-qn');
    if (!numEl) return;
    const current = qtyTarget.has(key) ? qtyTarget.get(key) : (parseInt(numEl.textContent, 10) || 1);
    const next = Math.max(1, current + dir);        // the ✕ owns removal, never the minus
    if (next === current) return;
    qtyTarget.set(key, next);
    numEl.textContent = next;

    const minus = ctrl.querySelector('[data-cd-step="-1"]');
    if (minus) {
      const off = next <= 1;
      minus.classList.toggle('is-disabled', off);
      if (off) { minus.setAttribute('aria-disabled', 'true'); minus.setAttribute('tabindex', '-1'); }
      else { minus.removeAttribute('aria-disabled'); minus.removeAttribute('tabindex'); }
    }
    updateCartCount(drawerQtySum());

    clearTimeout(qtyTimer.get(key));
    qtyTimer.set(key, setTimeout(() => flushLine(key), 300));
  }

  async function flushLine(key) {
    const target = qtyTarget.get(key);
    qtyTarget.delete(key);
    qtyTimer.delete(key);
    if (target == null) return;
    const body = document.querySelector('[data-cart-drawer-body]');
    body && body.setAttribute('aria-busy', 'true');
    try {
      const c = await changeCartByKey(key, target);
      updateCartCount(c.item_count);
      await renderCartDrawer(c);
    } catch (err) {
      /* Never leave an invented number on screen: fall back to whatever the server says. */
      try { await renderCartDrawer(await fetchCart()); } catch (e2) { /* drawer keeps its last good state */ }
    } finally {
      const b = document.querySelector('[data-cart-drawer-body]');
      b && b.removeAttribute('aria-busy');
    }
  }

  let cartBusy = false;
  if (cartDrawer) {
    cartDrawer.addEventListener('click', async (e) => {
      const step = e.target.closest('[data-cd-line]');
      if (step) {
        e.preventDefault();
        if (step.getAttribute('aria-disabled') === 'true') return;
        const stepKey = step.getAttribute('data-cd-key');
        const dir = parseInt(step.getAttribute('data-cd-step'), 10);

        /* A +/- tap repaints immediately and syncs once the tapping stops — see stepLine. */
        if (stepKey && !isNaN(dir)) { stepLine(step, stepKey, dir); return; }

        /* The ✕ removes a line, which changes the list itself, so it stays synchronous. */
        if (cartBusy) return;
        const line = parseInt(step.dataset.cdLine, 10);
        const qty = parseInt(step.dataset.cdQty, 10);
        if ((!line && !stepKey) || isNaN(qty)) return;
        const body = document.querySelector('[data-cart-drawer-body]');
        cartBusy = true;
        body && body.setAttribute('aria-busy', 'true');
        try {
          const c = stepKey ? await changeCartByKey(stepKey, qty) : await changeCart(line, qty);
          updateCartCount(c.item_count);
          await renderCartDrawer(c);
        } catch (err) {
          /* Leave the drawer as it stands and let the link work the old way. */
          window.location.href = step.href;
        } finally {
          cartBusy = false;
          const fresh = document.querySelector('[data-cart-drawer-body]');
          fresh && fresh.removeAttribute('aria-busy');
        }
        return;
      }

      const xs = e.target.closest('[data-cd-xs-add]');
      if (xs) {
        e.preventDefault();
        if (cartBusy || xs.disabled) return;
        const id = Number(xs.getAttribute('data-variant-id'));
        if (!id) return;
        const label = xs.innerHTML;
        cartBusy = true;
        xs.disabled = true;
        xs.textContent = 'Adding…';
        try {
          await addToCart([{ id, quantity: 1 }]);
          const c = await fetchCart();
          updateCartCount(c.item_count);
          await renderCartDrawer(c);
        } catch (err) {
          xs.innerHTML = label;
          xs.disabled = false;
        } finally {
          cartBusy = false;
        }
      }
    });
  }

  // Load initial cart count
  fetchCart().then((c) => updateCartCount(c.item_count)).catch(() => {});

  // ---------- Product form (in-place add) ----------
  document.querySelectorAll('form[data-product-form]').forEach((form) => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = form.querySelector('[type="submit"]');
      const label = btn && btn.querySelector('[data-atc-label]');
      const setText = (t) => { if (label) label.textContent = t; else if (btn) btn.textContent = t; };
      const orig = label ? label.textContent : (btn && btn.textContent);
      if (btn) { btn.disabled = true; setText('Adding...'); }
      try {
        const fd = new FormData(form);
        const item = {
          id: parseInt(fd.get('id'), 10),
          quantity: parseInt(fd.get('quantity') || '1', 10),
        };
        await addToCart(item);
        const c = await fetchCart();
        updateCartCount(c.item_count);
        // Buy it now: same add path, straight to checkout instead of the drawer.
        if (e.submitter && e.submitter.hasAttribute('data-buy-now')) {
          fsGoToCheckout();
          return;
        }
        await renderCartDrawer(c);
        cartDrawer && cartDrawer._open && cartDrawer._open();
        // Let bundle picker restore the totalized label
        const bp = document.querySelector('[data-bundle-picker]');
        if (bp) bp.dispatchEvent(new CustomEvent('fs:variant-change'));
        else setText(orig || 'Add to cart');
      } catch (err) {
        alert(err.message);
        setText(orig || 'Add to cart');
      } finally {
        if (btn) btn.disabled = false;
      }
    });
  });

  // ---------- Product variant/swatch ----------
  /* Shopify CDN urls already carry a ?v= cache key, so a size has to be appended with & not ?. */
  function fsImgUrl(src, width) {
    if (!src) return '';
    var u = src.indexOf('//') === 0 ? 'https:' + src : src;
    return u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /* Point whatever media the section owns at the chosen variant.
     Two shapes are supported: the homepage card, which is a single [data-variant-image], and the
     PDP gallery, which has a main image plus thumbs. For the gallery the matching THUMB is
     activated rather than the main image being set directly — that keeps the thumb highlight
     honest instead of leaving it pointing at the previous colour. */
  function fsSyncVariantImage(picker, v) {
    var root = picker.closest('.shopify-section') || document;

    var flat = root.querySelector('[data-variant-image]');
    if (flat && v.featured_image && v.featured_image.src) {
      var src = v.featured_image.src;
      flat.src = fsImgUrl(src, 900);
      if (flat.srcset) {
        flat.srcset = fsImgUrl(src, 500) + ' 500w, ' + fsImgUrl(src, 800) + ' 800w, ' + fsImgUrl(src, 1200) + ' 1200w';
      }
      if (v.featured_image.alt) flat.alt = v.featured_image.alt;
      return;
    }

    var gallery = root.querySelector('[data-gallery]');
    if (!gallery) return;
    var mediaId = v.featured_media && v.featured_media.id;
    var thumbs = gallery.querySelectorAll('.gallery__thumb');
    if (mediaId) {
      for (var i = 0; i < thumbs.length; i++) {
        if (String(thumbs[i].dataset.mediaId) === String(mediaId)) { thumbs[i].click(); return; }
      }
    }
    /* No thumb for it (media not in the first 6, or none assigned) — set the main image anyway. */
    var main = gallery.querySelector('.gallery__main img');
    if (main && v.featured_image && v.featured_image.src) {
      /* Drop the first photo's srcset, or it would win over the new src. */
      main.removeAttribute('srcset');
      main.src = fsImgUrl(v.featured_image.src, 1200);
      if (v.featured_image.alt) main.alt = v.featured_image.alt;
    }
  }

  document.querySelectorAll('[data-variant-picker]').forEach((picker) => {
    // Locate targets across the whole product form area (not just the fieldset ancestor).
    const form = document.querySelector('form[data-product-form]') || picker.closest('form');
    const scope = document.querySelector('.product-form') || document;
    const priceEl = scope.querySelector('[data-price]');
    const compareEl = scope.querySelector('[data-compare-at]');
    const saveEl = scope.querySelector('[data-save]');
    const savedEl = scope.querySelector('[data-saved]');
    // Every form in .product-form has its own hidden `id` — keep them all in sync.
    const idEls = scope.querySelectorAll('input[name="id"]');
    const btn = form && form.querySelector('[type="submit"]');
    const variants = JSON.parse(picker.dataset.variants || '[]');
    picker.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.addEventListener('change', () => {
        const v = variants.find((x) => String(x.id) === input.value);
        if (!v) return;
        picker.querySelectorAll('.swatch').forEach((s) => s.classList.toggle('is-active', s.contains(input)));
        // Update the selected value display in the legend
        const swatchLabel = input.closest('.swatch');
        const selectedValueEl = picker.querySelector('[data-selected-value]');
        if (swatchLabel && selectedValueEl) {
          const val = swatchLabel.getAttribute('title');
          if (val) selectedValueEl.textContent = val;
        }
        fsSyncVariantImage(picker, v);
        idEls.forEach((el) => { el.value = v.id; });
        if (priceEl) priceEl.textContent = money(v.price);
        if (compareEl) {
          if (v.compare_at_price && v.compare_at_price > v.price) {
            compareEl.hidden = false;
            compareEl.textContent = money(v.compare_at_price);
          } else {
            compareEl.hidden = true;
          }
        }
        if (saveEl) {
          if (v.compare_at_price && v.compare_at_price > v.price) {
            const pct = Math.round(((v.compare_at_price - v.price) / v.compare_at_price) * 100);
            saveEl.hidden = false; saveEl.textContent = 'Save ' + pct + '%';
          } else {
            saveEl.hidden = true;
          }
        }
        if (savedEl) {
          if (v.compare_at_price && v.compare_at_price > v.price) {
            savedEl.hidden = false;
            savedEl.textContent = 'You save ' + money(v.compare_at_price - v.price);
          } else {
            savedEl.hidden = true;
          }
        }
        if (btn) {
          btn.disabled = !v.available;
          const label = btn.querySelector('[data-atc-label]');
          const text = v.available ? (btn.dataset.available || 'Add to cart') : 'Sold out';
          if (label) { label.textContent = text; } else { btn.textContent = text; }
        }
        // Jump gallery to this variant's featured media if we have one
        const mediaId = v.featured_media && v.featured_media.id;
        if (mediaId) {
          const thumb = document.querySelector('.gallery__thumb[data-media-id="' + mediaId + '"]');
          if (thumb) thumb.click();
        }
      });
    });
  });

  // ---------- Product gallery (simple: swap the single main img src) ----------
  document.querySelectorAll('[data-gallery]').forEach((g) => {
    const mainImg = g.querySelector('.gallery__main img');
    if (!mainImg) return;
    const thumbs = g.querySelectorAll('.gallery__thumb');
    thumbs.forEach((t) => {
      t.addEventListener('click', () => {
        const src = t.dataset.thumbSrc;
        if (!src) return;
        /* The main photo carries a srcset (snippets/hero-srcset.liquid), and a srcset beats src,
           so swapping src alone would leave the first photo showing. Swap both, srcset first. */
        if (t.dataset.thumbSrcset) mainImg.srcset = t.dataset.thumbSrcset;
        else mainImg.removeAttribute('srcset');
        mainImg.src = src;
        if (t.dataset.thumbAlt) mainImg.alt = t.dataset.thumbAlt;
        thumbs.forEach((x) => x.classList.toggle('is-active', x === t));
      });
    });
  });

  /* ---------- Lazy video ----------
     Every <video> in this theme used to carry `autoplay muted loop playsinline preload="metadata"`.
     AUTOPLAY OVERRIDES PRELOAD: the browser must fetch enough to start playing, so "metadata" was
     ignored and the files downloaded in full on load. On the product page that was 8.07 MB across
     two clips (2.95 + 5.12), BOTH of which sit below the fold and most visitors never reach.
     They now ship `preload="none"` with no autoplay, and start when they scroll into view — which
     for an above-the-fold hero is immediately, so nothing looks different there either.
     `play()` is wrapped because browsers reject it when the tab is backgrounded or data-saver is
     on; a rejected promise here is not an error worth surfacing. */
  (function () {
    var vids = document.querySelectorAll('video[data-lazy-video]');
    if (!vids.length) return;
    /* NOTE: videos autoplay for EVERYONE, including visitors with prefers-reduced-motion set.
       That is Ahmad's explicit call (2026-08-20) — the clip is the main selling asset and he does
       not want it gated behind a tap. A reduced-motion branch was added and then removed here on
       purpose; do not "restore" it. What keeps this defensible is the pause control added further
       down this file (WCAG 2.2.2 wants a pause MECHANISM for anything moving over five seconds,
       not necessarily no motion at all) — so if that control is ever removed, this decision
       becomes a real accessibility problem rather than a trade-off. */
    if (!('IntersectionObserver' in window)) {
      // No observer: fall back to loading them normally rather than never playing.
      vids.forEach(function (v) { v.preload = 'metadata'; v.play().catch(function () {}); });
      return;
    }
    var vo = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var v = e.target;
        if (e.isIntersecting) {
          /* First contact: the element has preload="none", so readyState is 0 and there is nothing
             to play yet. Calling play() at that instant is rejected — observed live. Kick the fetch
             with load(), try immediately anyway (cheap if it works), and retry once the browser
             says it can play. `once: true` so a looping video never stacks listeners. */
          if (v.preload !== 'auto') {
            v.preload = 'auto';
            try { v.load(); } catch (err) { /* nothing to recover from */ }
          }
          var attempt = v.play();
          if (attempt && attempt.catch) {
            attempt.catch(function () {
              v.addEventListener('canplay', function () {
                v.play().catch(function () {});
              }, { once: true });
            });
          }
        } else if (!v.paused) {
          v.pause();   // off screen: stop decoding, it is not being watched
        }
      });
    }, { rootMargin: '200px 0px', threshold: 0.01 });
    vids.forEach(function (v) { vo.observe(v); });
  })();

  // ---------- Sticky ATC ----------
  const stickyAtc = document.querySelector('.sticky-atc');
  const stickyTarget = document.querySelector('[data-sticky-atc-target]');
  if (stickyAtc && stickyTarget && 'IntersectionObserver' in window) {
    let atcOffScreen = false;
    let footerVisible = false;
    // The bar ships `inert` and only drops it when it is actually on screen. It used to
    // ship aria-hidden="true" permanently, which hid a real, focusable Add to cart button
    // from screen readers while leaving it in the tab order.
    const apply = () => {
      const shown = atcOffScreen && !footerVisible;
      stickyAtc.classList.toggle('is-visible', shown);
      stickyAtc.inert = !shown;
    };
    const atcObs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { atcOffScreen = !e.isIntersecting; });
      apply();
    }, { threshold: 0 });
    atcObs.observe(stickyTarget);
    const footer = document.querySelector('.site-footer');
    if (footer) {
      const footObs = new IntersectionObserver((entries) => {
        entries.forEach((e) => { footerVisible = e.isIntersecting; });
        apply();
      }, { threshold: 0, rootMargin: '0px 0px -60px 0px' });
      footObs.observe(footer);
    }
  }

  // ---------- Quantity buttons ----------
  document.querySelectorAll('.qty').forEach((q) => {
    const input = q.querySelector('input');
    q.querySelectorAll('button').forEach((b) => {
      b.addEventListener('click', () => {
        const delta = parseInt(b.dataset.delta || '1', 10);
        const v = Math.max(1, parseInt(input.value || '1', 10) + delta);
        input.value = v;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  });

  /* ---------- Cart PAGE: apply quantity changes immediately ----------
     The stepper above only edits the input; on the cart page nothing then applied it, so the
     number moved while the line price, the savings and the order summary stayed put and the cart
     looked broken until you found the "Update quantities" button. Now the change is POSTed and the
     whole section is re-rendered from the server, the same approach the drawer uses.
     The form and its update button remain as the no-JS fallback. */
  const cartPage = document.querySelector('[data-cart-page]');
  if (cartPage) {
    let cartPageBusy = false;

    async function refreshCartPage() {
      const res = await fetch(CART_PAGE_URL, { headers: { Accept: 'text/html' }, cache: 'no-store' });
      if (!res.ok) throw new Error('cart ' + res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const next = doc.querySelector('[data-cart-page]');
      if (!next) throw new Error('cart markup missing');
      const host = document.querySelector('[data-cart-page]');
      host.innerHTML = next.innerHTML;
    }

    /* Clicks are absorbed, not dropped. A stepper has to feel instant, so the input updates
       immediately and the server sync is debounced to the LAST value per line. Dropping clicks on
       a busy flag made the totals trail the number on screen, which is the bug this whole block
       exists to fix. */
    const pending = new Map();
    const timers = new Map();
    function queueLineChange(key, qty) {
      pending.set(key, qty);
      clearTimeout(timers.get(key));
      timers.set(key, setTimeout(() => {
        const target = pending.get(key);
        pending.delete(key);
        applyLineChange(key, target);
      }, 350));
    }

    async function applyLineChange(key, qty) {
      if (cartPageBusy) { queueLineChange(key, qty); return; }
      cartPageBusy = true;
      const host = document.querySelector('[data-cart-page]');
      host.setAttribute('aria-busy', 'true');
      try {
        /* /cart/change.js takes the line item KEY as `id`, which is exactly what the input's
           updates[<key>] name already carries — no line-index bookkeeping needed, so this cannot
           drift when a line is removed. */
        const c = await changeCartByKey(key, qty);
        updateCartCount(c.item_count);
        await fsAutoApplyBundle(c);        // keep the tier honest as quantities move
        await refreshCartPage();
        const fresh = await fetchCart();
        updateCartCount(fresh.item_count);
        await renderCartDrawer(fresh);     // keep the drawer in step with the page
      } catch (e) {
        window.location.reload();          // fail safe: never leave a stale cart on screen
      } finally {
        cartPageBusy = false;
        const h = document.querySelector('[data-cart-page]');
        h && h.removeAttribute('aria-busy');
      }
    }

    const keyFrom = (input) => {
      const m = (input.getAttribute('name') || '').match(/^updates\[(.+)\]$/);
      return m ? m[1] : null;
    };

    /* The delta is computed HERE rather than read back off the input. The generic `.qty` stepper is
       bound once at page load, so after the first re-render its buttons are new elements with no
       handler and input.value would never move again — the second click onwards would re-send the
       same number. Delegation on the section survives every swap. */
    cartPage.addEventListener('click', (e) => {
      const btn = e.target.closest('.qty button[data-delta]');
      if (!btn) return;
      const input = btn.parentElement.querySelector('input');
      const key = input && keyFrom(input);
      if (!key) return;
      e.preventDefault();
      /* Bail BEFORE touching the input. Setting it optimistically and then having applyLineChange
         drop the call on its busy guard left the field showing a number that was never sent, so
         the totals trailed the stepper by one click. */
      const delta = parseInt(btn.dataset.delta || '1', 10);
      const next = Math.max(0, parseInt(input.value || '1', 10) + delta);
      input.value = next;
      queueLineChange(key, next);
    });

    cartPage.addEventListener('change', (e) => {
      const input = e.target.closest('.qty input[name^="updates["]');
      if (!input) return;
      const key = keyFrom(input);
      if (key) queueLineChange(key, Math.max(0, parseInt(input.value || '1', 10)));
    });
  }

  // ---------- Bundle picker ----------
  document.querySelectorAll('[data-bundle-picker]').forEach((picker) => {
    const form = document.querySelector('form[data-product-form]');
    if (!form) return;
    // Both the AJAX ATC form and the Buy It Now dynamic checkout form have their
    // own quantity input; keep every one of them in sync with the selected pack.
    const scope = document.querySelector('.product-form') || document;
    const qtyInputs = scope.querySelectorAll('input[name="quantity"]');
    const atcBtn = form.querySelector('[type="submit"]');
    const atcLabel = atcBtn && atcBtn.querySelector('[data-atc-label]');
    const stickyPrice = document.querySelector('.sticky-atc__price');

    const render = () => {
      const selected = picker.querySelector('input[type="radio"]:checked');
      const unitCents = parseInt(picker.dataset.unitCents || '0', 10);
      if (!selected || !unitCents) return;
      const selQty = parseInt(selected.value, 10) || 1;
      const selTier = selected.closest('.bundle-tier');
      const selDiscount = parseFloat(selTier && selTier.dataset.discount || '0');
      /* MATCH SHOPIFY'S ROUNDING EXACTLY. Verified against real carts 2026-08-19 (International,
         unit 2499): Shopify charges 2250 / 2125 / 2000 per unit at 10/15/20%, i.e. it FLOORS the
         discount amount and subtracts it. `Math.round(unit * (100-pct)/100)` gave 2249 / 2124 /
         1999 — one cent low per unit, so the page quoted $44.98 / $63.72 / $79.96 while checkout
         charged $45.00 / $63.75 / $80.00. Under-quoting is still quoting a price you do not honour.
         Lebanon (unit 1500) divided evenly and hid the bug. */
      const selUnit = unitCents - Math.floor(unitCents * selDiscount / 100);
      const selTotal = selUnit * selQty;

      // Sync quantity into every product form
      qtyInputs.forEach((el) => { el.value = selQty; });

      // Update ATC label with total
      if (atcLabel && atcBtn && !atcBtn.disabled) {
        atcLabel.textContent = (atcBtn.dataset.available || 'Add to cart') + ' · ' + money(selTotal);
      }
      if (stickyPrice) stickyPrice.textContent = money(selTotal);

      // Refresh every tier's displayed price from current unit cents
      picker.querySelectorAll('.bundle-tier').forEach((tier) => {
        const tierRadio = tier.querySelector('input[type="radio"]');
        const q = parseInt(tierRadio && tierRadio.value || '1', 10);
        const d = parseFloat(tier.dataset.discount || '0');
        const mode = tier.dataset.display || 'total';
        const u = unitCents - Math.floor(unitCents * d / 100);   // same rule as selUnit above
        const priceEl = tier.querySelector('[data-tier-price]');
        if (priceEl) priceEl.textContent = money(mode === 'each' ? u : u * q);
        tier.classList.toggle('is-selected', tier.contains(selected));
      });
    };

    picker.querySelectorAll('input[type="radio"]').forEach((r) => {
      r.addEventListener('change', render);
    });
    picker.addEventListener('fs:variant-change', render);
    render();
  });

  // Bridge variant swatch picker → bundle picker (recompute totals on color change)
  document.querySelectorAll('[data-variant-picker]').forEach((picker) => {
    const form = picker.closest('form') || document.querySelector('form[data-product-form]');
    const bundle = document.querySelector('[data-bundle-picker]');
    if (!form || !bundle) return;
    const variants = JSON.parse(picker.dataset.variants || '[]');
    picker.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.addEventListener('change', () => {
        const v = variants.find((x) => String(x.id) === input.value);
        if (!v) return;
        bundle.dataset.unitCents = String(v.price);
        bundle.dispatchEvent(new CustomEvent('fs:variant-change'));
      });
    });
  });

  // ---------- Tabs (product tabs) ----------
  document.querySelectorAll('[data-tabs]').forEach((tabs) => {
    const tabButtons = tabs.querySelectorAll('.tabs__tab');
    const panels = tabs.querySelectorAll('.tabs__panel');
    const activate = (targetId) => {
      tabButtons.forEach((b) => {
        const isActive = b.getAttribute('data-tab-target') === targetId;
        b.classList.toggle('is-active', isActive);
        b.setAttribute('aria-selected', isActive ? 'true' : 'false');
        b.setAttribute('tabindex', isActive ? '0' : '-1');
      });
      panels.forEach((p) => {
        const isActive = p.id === targetId;
        p.classList.toggle('is-active', isActive);
        if (isActive) { p.removeAttribute('hidden'); } else { p.setAttribute('hidden', ''); }
      });
      /* The tablist is a single sideways scroller on phones now, so an activated tab can sit
         off-screen — most visibly when the #reviews anchor opens the Reviews tab from elsewhere
         on the page. inline: 'nearest' means an already-visible tab does not jump. */
      const active = tabs.querySelector('.tabs__tab.is-active');
      const nav = tabs.querySelector('.tabs__nav');
      if (active && nav && nav.scrollWidth > nav.clientWidth) {
        const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        active.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: smooth ? 'smooth' : 'auto' });
      }
    };
    tabButtons.forEach((btn, i) => {
      btn.addEventListener('click', () => activate(btn.dataset.tabTarget));
      btn.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          const dir = e.key === 'ArrowRight' ? 1 : -1;
          const next = (i + dir + tabButtons.length) % tabButtons.length;
          tabButtons[next].focus();
          activate(tabButtons[next].dataset.tabTarget);
        }
      });
    });
  });

  // ---------- Hide reviews section when Okendo widget stays empty ----------
  document.querySelectorAll('[data-hide-if-empty]').forEach((section) => {
    const widget = section.querySelector('.okendo-reviews-widget, [data-oke-widget]');
    if (!widget) return;
    const check = () => {
      /* DEADLOCK GUARD. `is-empty` hides the section, and a hidden element reports
         offsetHeight 0 — so once this ran before Okendo finished, the height could never grow
         again and the section stayed hidden forever. Okendo populated 56KB of markup on the
         Ice Bucket page and the reviews were still invisible.
         Un-hide first, measure, then re-apply only if it is genuinely empty. */
      const wasEmpty = section.classList.contains('is-empty');
      if (wasEmpty) section.classList.remove('is-empty');
      const populated = widget.children.length > 0 && widget.offsetHeight > 24;
      section.classList.toggle('is-empty', !populated);
    };
    // Give Okendo up to 4s to render before declaring the section empty.
    setTimeout(check, 1500);
    setTimeout(check, 4000);
    if ('MutationObserver' in window) {
      const mo = new MutationObserver(check);
      mo.observe(widget, { childList: true, subtree: true });
      setTimeout(() => mo.disconnect(), 8000);
    }
  });
})();

/* ==========================================================================
   PDP buy-box modules — bundle savings line, mix & match multipack, cross-sell.
   Appended as a self-contained IIFE. Nothing above this line is modified.
   The multipack add path attaches on the CAPTURE phase and only stops the
   existing submit handler when a valid mix is staged; every other case falls
   through to the theme's normal single-variant flow. All work is wrapped in
   try/catch so a failure here can never block Add to cart.
   ========================================================================== */
(function () {
  'use strict';
  var money = function (cents) {
    try { return (window.Shopify && Shopify.formatMoney)
      ? Shopify.formatMoney(cents, window.themeMoneyFormat || '${{amount}}')
      : '$' + (cents / 100).toFixed(2); }
    catch (e) { return '$' + (cents / 100).toFixed(2); }
  };

  var picker = document.querySelector('[data-bundle-picker]');
  var panel  = document.querySelector('[data-mp-panel]');
  var form   = document.querySelector('form[data-product-form]');
  var line   = document.querySelector('[data-bundle-savings]');

  /* ---------- 1. Savings line under the bundle tiers ---------- */
  function selectedTier() {
    if (!picker) return null;
    var input = picker.querySelector('input[name="fs-pack"]:checked');
    return input ? input.closest('.bundle-tier') : null;
  }
  function unitCents() {
    return parseInt((picker && picker.getAttribute('data-unit-cents')) || '0', 10) || 0;
  }
  function renderSavings() {
    if (!line) return;
    var tier = selectedTier();
    if (!tier) { line.hidden = true; return; }
    var qty = parseInt(tier.getAttribute('data-qty') || '1', 10);
    var pct = parseFloat(tier.getAttribute('data-discount') || '0');
    if (!pct || pct <= 0) { line.hidden = true; return; }
    var saved = Math.round(unitCents() * qty * (pct / 100));
    line.textContent = "You're saving " + money(saved) + ' on this pack';
    line.hidden = false;
  }

  /* ---------- 2. Mix & match multipack ---------- */
  var mpMin = panel ? parseInt(panel.getAttribute('data-mp-min') || '0', 10) : 0;
  var mpPct = panel ? parseFloat(panel.getAttribute('data-mp-discount') || '0') : 0;

  function mpRows() { return panel ? Array.prototype.slice.call(panel.querySelectorAll('[data-mp-row]')) : []; }
  function mpCountOf(row) { return parseInt(row.querySelector('[data-mp-count]').textContent, 10) || 0; }
  function mpTotal() { return mpRows().reduce(function (n, r) { return n + mpCountOf(r); }, 0); }

  function mpIsActive() {
    var tier = selectedTier();
    if (!tier || !panel) return false;
    return parseInt(tier.getAttribute('data-qty') || '1', 10) >= mpMin && mpMin > 1;
  }

  function mpRender() {
    if (!panel) return;
    var total = mpTotal();
    var totalEl = panel.querySelector('[data-mp-total]');
    var badge = panel.querySelector('[data-mp-badge]');
    if (totalEl) totalEl.textContent = total;
    mpRows().forEach(function (r) {
      r.querySelector('[data-mp-step="-1"]').disabled = mpCountOf(r) === 0;
    });
    if (badge) {
      if (total >= mpMin) {
        badge.textContent = 'Save ' + mpPct + '%';
        badge.classList.add('is-unlocked');
      } else {
        badge.textContent = 'Add ' + (mpMin - total) + ' more to unlock ' + mpPct + '% off';
        badge.classList.remove('is-unlocked');
      }
    }
    panel.hidden = !mpIsActive();
  }

  if (panel) {
    panel.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-mp-step]');
      if (!btn) return;
      e.preventDefault();
      var row = btn.closest('[data-mp-row]');
      var el = row.querySelector('[data-mp-count]');
      var next = Math.max(0, (parseInt(el.textContent, 10) || 0) + parseInt(btn.getAttribute('data-mp-step'), 10));
      el.textContent = next;
      mpRender();
    });
  }

  /* ---------- 2b. Per-cup colour pickers (the 2- and 3-cup packs) ----------
     The bundle picker can only set `quantity`, which adds N of ONE variant. These rows let
     cups 2..N each carry their own colour; cup 1 stays the main swatch above. Rows the
     customer has not touched follow the main colour, so picking "3" and hitting add is still
     one click for someone who wants three of the same. */
  var cupPanel = document.querySelector('[data-cup-panel]');
  var cupMax = cupPanel ? parseInt(cupPanel.getAttribute('data-cup-max') || '0', 10) : 0;

  function tierQty() {
    var tier = selectedTier();
    return tier ? (parseInt(tier.getAttribute('data-qty') || '1', 10) || 1) : 1;
  }
  function cupIsActive() {
    if (!cupPanel || cupMax < 2) return false;
    var q = tierQty();
    return q >= 2 && q <= cupMax;
  }
  function cupRows() {
    return cupPanel ? Array.prototype.slice.call(cupPanel.querySelectorAll('[data-cup-row]')) : [];
  }
  function mainVariantId() {
    var el = document.querySelector('form[data-product-form] input[name="id"]');
    return el ? (Number(el.value) || 0) : 0;
  }
  /* Untouched rows mirror whatever colour cup 1 is on. */
  function cupSyncUntouched() {
    var main = String(mainVariantId());
    cupRows().forEach(function (row) {
      if (row.getAttribute('data-touched')) return;
      var match = row.querySelector('input[type="radio"][value="' + main + '"]');
      if (match) match.checked = true;
    });
  }
  function cupRender() {
    if (!cupPanel) return;
    var active = cupIsActive();
    cupPanel.hidden = !active;
    if (!active) return;
    var q = tierQty();
    cupSyncUntouched();
    cupRows().forEach(function (row) {
      var idx = parseInt(row.getAttribute('data-cup-index') || '0', 10);
      row.hidden = idx > q;
      var checked = row.querySelector('input[type="radio"]:checked');
      Array.prototype.forEach.call(row.querySelectorAll('.cup-sw'), function (l) {
        l.classList.toggle('is-active', !!checked && l.contains(checked));
      });
      var nameEl = row.querySelector('[data-cup-name]');
      if (nameEl && checked) nameEl.textContent = checked.getAttribute('data-color') || '';
    });
  }
  /* One line per distinct colour, quantities aggregated. */
  function cupItems() {
    var q = tierQty();
    var main = mainVariantId();
    var counts = {};
    if (main) counts[main] = 1;                       // cup 1
    cupRows().forEach(function (row) {
      var idx = parseInt(row.getAttribute('data-cup-index') || '0', 10);
      if (!idx || idx > q) return;
      var checked = row.querySelector('input[type="radio"]:checked');
      var id = checked ? Number(checked.value) : main;
      if (!id) return;
      counts[id] = (counts[id] || 0) + 1;
    });
    return Object.keys(counts).map(function (id) {
      return { id: Number(id), quantity: counts[id] };
    });
  }

  /* The main colour lives in input[name="id"], which the variant picker's own handler updates.
     `fs:variant-change` is dispatched ON the bundle picker element and CustomEvent does not
     bubble, so the document-level listener further down never sees it — listen to the swatches
     directly instead, one tick later so their handler lands first. */
  Array.prototype.forEach.call(document.querySelectorAll('[data-variant-picker]'), function (vp) {
    vp.addEventListener('change', function () { setTimeout(cupRender, 0); });
  });
  if (picker) { picker.addEventListener('fs:variant-change', cupRender); }

  if (cupPanel) {
    cupPanel.addEventListener('change', function (e) {
      var input = e.target.closest ? e.target.closest('input[type="radio"]') : null;
      if (!input) return;
      var row = input.closest('[data-cup-row]');
      if (row) row.setAttribute('data-touched', '1');
      cupRender();
    });
  }

  /* Capture-phase interception: only fires for a staged, valid mix. */
  if (form) {
    form.addEventListener('submit', function (e) {
      try {
        var items = null;
        if (mpIsActive()) {
          if (mpTotal() < mpMin) return;        // fall through to the normal handler
          items = mpRows()
            .filter(function (r) { return mpCountOf(r) > 0; })
            .map(function (r) { return { id: Number(r.getAttribute('data-variant-id')), quantity: mpCountOf(r) }; });
        } else if (cupIsActive()) {
          items = cupItems();
          /* Only take over when the pack really is mixed AND adds up. A single-colour pack is
             left to the plain quantity path that already works, and anything that does not
             total the selected pack size falls through rather than adding the wrong cart. */
          var want = tierQty();
          var got = items.reduce(function (n, i) { return n + i.quantity; }, 0);
          if (items.length < 2 || got !== want) return;
        } else { return; }
        if (!items || !items.length) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        var btn = form.querySelector('button[type=submit]');
        var label = btn && btn.querySelector('[data-atc-label]');
        var was = label && label.textContent;
        if (btn) btn.disabled = true;
        if (label) label.textContent = 'Adding…';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items })
        })
          .then(function (r) { if (!r.ok) throw new Error('cart'); return r.json(); })
          .then(function () {
            var buyNow = e.submitter && e.submitter.hasAttribute('data-buy-now');
            if (buyNow && window.fsGoToCheckout) { window.fsGoToCheckout(); }
            else { window.location.href = buyNow ? '/checkout' : '/cart'; }
          })
          .catch(function () {
            if (btn) btn.disabled = false;
            if (label) label.textContent = was || 'Add to cart';
            window.alert('Sorry, that mix could not be added. Please try again.');
          });
      } catch (err) { /* fail open: the theme's own handler still runs */ }
    }, true);
  }

  /* ---------- 3. Cross-sell add buttons ---------- */
  var cyo = document.querySelector('.cyo');
  if (cyo) {
    var err = cyo.querySelector('[data-cyo-error]');
    cyo.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-cyo-add]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      var id = Number(btn.getAttribute('data-variant-id'));
      if (!id) return;
      var original = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';
      if (err) err.hidden = true;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
      })
        .then(function (r) { if (!r.ok) throw new Error('cart'); return r.json(); })
        .then(function () {
          btn.textContent = 'Added';
          btn.classList.add('is-added');
          document.dispatchEvent(new CustomEvent('fs:cart-updated'));
        })
        .catch(function () {
          btn.disabled = false;
          btn.textContent = original;
          if (err) { err.textContent = 'Could not add that item. Please try again.'; err.hidden = false; }
        });
    });
  }

  /* ---------- wire up ---------- */
  if (picker) {
    picker.addEventListener('change', function () { renderSavings(); mpRender(); cupRender(); });
    picker.addEventListener('click', function () { setTimeout(function () { renderSavings(); mpRender(); cupRender(); }, 0); });
  }
  document.addEventListener('fs:variant-change', function () { renderSavings(); mpRender(); cupRender(); });
  renderSavings();
  mpRender();
  cupRender();
})();

/* ---------- Real Okendo reviews rendered into the theme's own card grid ----------
   Fetches the product's approved reviews and builds cards with textContent only
   (bodies are customer-authored, so they never go through innerHTML). Fails CLOSED:
   the grid ships hidden and empty, and the ONE line that reveals it runs only after
   real cards are appended. Reviews carrying photos are deliberately skipped here and
   left to Okendo's own widget behind "Read all reviews", which renders media properly.
   The "Verified purchase" badge is drawn ONLY when Okendo reports reviewer.isVerified. */
(function () {
  var grids = document.querySelectorAll('[data-okendo-cards]');
  if (!grids.length) return;

  var subscriberId = null;
  try {
    subscriberId = JSON.parse(document.getElementById('oke-reviews-settings').textContent).subscriberId;
  } catch (e) { /* app embed missing */ }
  if (!subscriberId) return;

  var AVATARS = ['#0f9d58', '#7c3aed', '#4338ca', '#0891b2', '#db2777', '#ea580c'];

  function initials(name) {
    var parts = String(name).split(/[^A-Za-z]+/).filter(Boolean);
    return (parts.slice(0, 2).map(function (p) { return p[0]; }).join('') || '?').toUpperCase();
  }
  function avatarColor(name) {
    var h = 0;
    for (var i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
    return AVATARS[h % AVATARS.length];
  }
  function star(on) {
    var s = document.createElement('span');
    s.className = 'star' + (on ? ' star--on' : '');
    s.textContent = on ? '★' : '☆';
    return s;
  }

  function card(r, showDates) {
    var el = document.createElement('article');
    el.className = 'rev-card';

    var stars = document.createElement('div');
    stars.className = 'rev-card__stars';
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', 'Rated ' + r.rating + ' out of 5');
    for (var i = 1; i <= 5; i++) stars.appendChild(star(i <= r.rating));
    el.appendChild(stars);

    if (r.title) {
      var h = document.createElement('h3');
      h.className = 'rev-card__title';
      h.textContent = r.title;
      el.appendChild(h);
    }
    if (r.body) {
      var p = document.createElement('p');
      p.className = 'rev-card__body';
      p.textContent = '“' + r.body + '”';
      el.appendChild(p);
    }

    var name = (r.reviewer && r.reviewer.displayName) || 'Anonymous';
    var foot = document.createElement('div');
    foot.className = 'rev-card__foot';

    var av = document.createElement('span');
    av.className = 'rev-card__avatar';
    av.style.background = avatarColor(name);
    av.textContent = initials(name);
    av.setAttribute('aria-hidden', 'true');
    foot.appendChild(av);

    var who = document.createElement('div');
    who.className = 'rev-card__who';
    var nm = document.createElement('span');
    nm.className = 'rev-card__name';
    nm.textContent = name;
    who.appendChild(nm);

    var meta = document.createElement('span');
    meta.className = 'rev-card__meta';
    if (r.reviewer && r.reviewer.isVerified === true) {
      var v = document.createElement('span');
      v.className = 'rev-card__verified';
      v.textContent = '✓ Verified purchase';
      meta.appendChild(v);
    }
    /* Dates are OFF unless the section explicitly asks for them (data-show-dates="true").
       Absent attribute means hidden, so a section that never opts in — the PDP tabs grid —
       gets the same treatment without needing an edit.
       The " · " separator is appended WITH the date rather than with the badge: built the
       other way round it is left dangling after "Verified purchase" the moment the date
       goes away, and on an unverified review the whole meta line would be an empty span. */
    if (showDates && r.dateCreated) {
      var d = new Date(r.dateCreated);
      if (!isNaN(d)) {
        if (meta.childNodes.length) meta.appendChild(document.createTextNode(' · '));
        meta.appendChild(document.createTextNode(
          d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })));
      }
    }
    if (meta.childNodes.length) who.appendChild(meta);
    foot.appendChild(who);
    el.appendChild(foot);
    return el;
  }

  // One grid per section, so scope each one's notice and toggle to its own section
  // rather than to the document. Otherwise a second instance silently drives the first.
  Array.prototype.forEach.call(grids, function (grid) {
    var scope = grid.closest('.shopify-section') || document;
    var note = scope.querySelector('[data-okendo-error]');
    var productId = grid.getAttribute('data-product-id');
    var limit = parseInt(grid.getAttribute('data-limit'), 10) || 12;
    if (!productId) return;

    // Over-fetch so the photo-review filter still leaves enough cards.
    var fetchCount = Math.max(limit * 3, 40);
    var url = 'https://api.okendo.io/v1/stores/' + encodeURIComponent(subscriberId) +
              '/products/' + encodeURIComponent(productId) + '/reviews?limit=' + fetchCount;

    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (res) { if (!res.ok) throw new Error('okendo ' + res.status); return res.json(); })
      .then(function (data) {
        var list = (data.reviews || []).filter(function (r) {
          return r && r.status === 'approved' && (r.title || r.body) &&
                 !(r.media && r.media.length);        // photo reviews live in the full widget
        }).slice(0, limit);
        if (!list.length) { if (note) note.hidden = false; return; }
        var frag = document.createDocumentFragment();
        // Opt-in per section: no attribute (or anything but "true") means no dates.
        var showDates = grid.getAttribute('data-show-dates') === 'true';
        list.forEach(function (r) { frag.appendChild(card(r, showDates)); });
        grid.appendChild(frag);
        grid.hidden = false;

        /* Phone clamp. Applied AFTER injection because the cards arrive asynchronously — a class
           set at render time would have nothing to clamp. Everything stays in the DOM; only the
           display is limited, so the markup a crawler sees is unchanged.
           There is no "show more" button any more: the clamp is permanent on phones and
           "Read all reviews" is the single way through to the full set. The clamp therefore
           must NOT be gated on a button existing, which is what it used to be. */
        if (list.length > 3) {
          var clamp = function () {
            grid.classList.toggle('is-clamped', window.matchMedia('(max-width: 699px)').matches);
          };
          clamp();
          window.addEventListener('resize', clamp);
        }
      })
      .catch(function () { if (note) note.hidden = false; });

    var toggle = scope.querySelector('[data-okendo-toggle]');
    var full = scope.querySelector('[data-okendo-full]');
    if (toggle && full) {
      toggle.addEventListener('click', function () {
        var open = full.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
  });
})();

/* ---------- Keep the header parked below the sticky announcement bar ----------
   The bar wraps to two lines on narrow screens, so its height cannot be hardcoded.
   Measured here and published as --ann-h, which the header's sticky `top` reads. */
(function () {
  var bar = document.querySelector('.announcement');
  if (!bar) return;
  var sync = function () {
    var h = bar.offsetHeight || 0;
    document.documentElement.style.setProperty('--ann-h', h + 'px');
  };
  sync();
  window.addEventListener('resize', sync);
  window.addEventListener('load', sync);
  if (window.ResizeObserver) new ResizeObserver(sync).observe(bar);
})();

/* ---------- #reviews deep link ----------
   The buy-box rating linked to #reviews, but the reviews live inside a tab panel whose id
   is section-scoped, so the anchor resolved to nothing and clicking the rating did nothing.
   The tabs section now carries a stable id="reviews"; this opens the Reviews tab as well,
   otherwise the anchor would land on a collapsed panel. */
(function () {
  function openReviews() {
    var host = document.getElementById('reviews');
    if (!host) return;
    var btn = host.querySelector('[data-tab-target^="panel-rev-"]');
    if (btn) btn.click();
    host.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href="#reviews"], a[href$="#reviews"]');
    if (!a) return;
    var sameDoc = !a.pathname || a.pathname === window.location.pathname;
    if (!sameDoc) return;              // let cross-page links navigate normally
    e.preventDefault();
    openReviews();
  });
  if (window.location.hash === '#reviews') setTimeout(openReviews, 300);
})();

/* ---------- Support launcher: stand down when a real chat widget shows up ----------
   Shopify Inbox (and every other chat app) injects its own bottom-corner button. Rather than
   guess one vendor's selector, watch for any fixed-position element anchored to the bottom
   corner that is not ours, and hide ours if one appears. Runs on a short poll because app
   embeds inject late, then stops. */
(function () {
  var fab = document.querySelector('[data-support-fab][data-yield-to-chat]');
  if (!fab) return;

  function chatWidgetPresent() {
    var vw = window.innerWidth, vh = window.innerHeight;
    var nodes = document.querySelectorAll('body > div, body > iframe, body > aside, body > section');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el === fab || el.contains(fab)) continue;
      var cs = window.getComputedStyle(el);
      if (cs.position !== 'fixed' || cs.display === 'none' || cs.visibility === 'hidden') continue;
      var r = el.getBoundingClientRect();
      if (r.width < 24 || r.height < 24) continue;
      // anchored to the bottom half, right-hand side — where chat launchers live
      var bottomAnchored = r.bottom > vh - 160;
      var rightAnchored = r.right > vw - 200;
      var id = (el.id || '') + ' ' + (typeof el.className === 'string' ? el.className : '');
      var looksLikeChat = /chat|inbox|messenger|support|widget/i.test(id);
      if (bottomAnchored && rightAnchored && (looksLikeChat || el.tagName === 'IFRAME')) return true;
    }
    return false;
  }

  var tries = 0;
  var timer = setInterval(function () {
    if (chatWidgetPresent()) { fab.hidden = true; clearInterval(timer); return; }
    if (++tries > 20) clearInterval(timer);      // ~10s, then give up quietly
  }, 500);
})();

/* ---------- Autoplaying video: reduced motion + a pause control ----------
   Seven looping videos autoplay across six sections. WCAG 2.2.2 needs a pause
   mechanism for anything that moves for more than five seconds, and
   prefers-reduced-motion has to actually stop them. Done here rather than in each
   section so a new autoplaying video is covered the moment it is added.
   Every one of these videos sits alone inside its own frame div, so the control
   can be appended to video.parentElement without disturbing any layout.        */
(function () {
  function initVideoControls() {
    /* WAS `video[autoplay]`. The lazy-video work removed the `autoplay` attribute from all eight
       videos in the theme, so this selector silently matched ZERO elements and the pause control
       — the whole point of this block, and the WCAG 2.2.2 mechanism for moving content — stopped
       being rendered anywhere. Match how the videos are actually marked up now. */
    var vids = document.querySelectorAll('video[data-lazy-video]:not([data-vid-bound]), video[autoplay]:not([data-vid-bound])');
    if (!vids.length) return;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    var PAUSE = '<svg class="vid-toggle__pause" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><rect x="3" y="2" width="3.5" height="12" rx="1" fill="currentColor"/><rect x="9.5" y="2" width="3.5" height="12" rx="1" fill="currentColor"/></svg>';
    var PLAY = '<svg class="vid-toggle__play" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M4 2.6v10.8c0 .8.9 1.3 1.5.8l7-5.4a1 1 0 0 0 0-1.6l-7-5.4c-.6-.5-1.5 0-1.5.8z" fill="currentColor"/></svg>';

    Array.prototype.forEach.call(vids, function (v) {
      var frame = v.parentElement;
      if (!frame) return;
      // Still skip anything a section has genuinely hidden, but reduced motion no longer hides
      // the hero video — it leaves it paused, which is exactly when this control matters most.
      if (window.getComputedStyle(v).display === 'none') return;
      v.setAttribute('data-vid-bound', '');
      frame.classList.add('has-vid-toggle');

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'vid-toggle';
      btn.innerHTML = PAUSE + PLAY;
      var name = v.getAttribute('aria-label') || 'video';

      var sync = function () {
        var playing = !v.paused;
        btn.dataset.state = playing ? 'playing' : 'paused';
        btn.setAttribute('aria-label', (playing ? 'Pause ' : 'Play ') + name);
      };
      btn.addEventListener('click', function () {
        if (v.paused) {
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        } else {
          v.pause();
        }
      });
      v.addEventListener('play', sync);
      v.addEventListener('pause', sync);

      if (reduce.matches) {
        v.removeAttribute('autoplay');
        v.autoplay = false;
        v.pause();
      }
      sync();
      frame.appendChild(btn);
    });

    var onChange = function (e) {
      if (!e.matches) return;
      Array.prototype.forEach.call(document.querySelectorAll('video[data-vid-bound]'), function (v) { v.pause(); });
    };
    if (reduce.addEventListener) reduce.addEventListener('change', onChange);
    else if (reduce.addListener) reduce.addListener(onChange);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVideoControls);
  } else {
    initVideoControls();
  }
  // Sections re-render in the theme editor; rebind so the control does not vanish.
  document.addEventListener('shopify:section:load', initVideoControls);
})();

/* ---------- Support chat ----------
   UI parity with the reference; the brain is ours. Two modes, decided by whether
   `chat_endpoint_url` is set — see snippets/support-chat.liquid for the reasoning.
   Everything is wrapped so that a failure here can never take down the rest of base.js. */
(function () {
  'use strict';
  var cfg = window.fsChatConfig;
  var win = document.getElementById('fs-chat-win');
  var bubble = document.getElementById('fs-chat-bubble');
  if (!cfg || !win || !bubble) return;

  var msgs = document.getElementById('fs-chat-msgs');
  var input = document.getElementById('fs-chat-input');
  var sendBtn = document.getElementById('fs-chat-send');
  var unread = document.getElementById('fs-unread');
  var nudge = document.getElementById('fs-chat-nudge');
  var statusEl = document.querySelector('[data-chat-status]');
  var humanBar = document.getElementById('fs-chat-human');

  var isOpen = false, greeted = false;
  var history = [];                 // assistant mode transcript
  var transcript = [];              // plain log, used as the email body
  var awaitingEmail = false, pendingQuestion = '', capturedEmail = '';
  var NUDGE_KEY = 'fs-chat-nudge-dismissed';

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }
  function now() {
    try { return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
    catch (e) { return ''; }
  }
  function addMsg(role, text) {
    var row = document.createElement('div');
    row.className = 'fs-msg fs-msg--' + role;
    row.innerHTML = '<div class="fs-msg__bubble">' + esc(text).replace(/\n/g, '<br>') +
                    '</div><div class="fs-msg__time">' + now() + '</div>';
    msgs.appendChild(row);
    msgs.scrollTop = msgs.scrollHeight;
    transcript.push((role === 'user' ? 'Customer' : 'Support') + ': ' + text);
    if (!isOpen && role === 'agent' && unread) unread.classList.add('is-visible');
  }
  function showTyping() {
    var row = document.createElement('div');
    row.className = 'fs-msg fs-msg--agent';
    row.id = 'fs-typing-row';
    row.innerHTML = '<div class="fs-typing"><span></span><span></span><span></span></div>';
    msgs.appendChild(row);
    msgs.scrollTop = msgs.scrollHeight;
  }
  function hideTyping() {
    var row = document.getElementById('fs-typing-row');
    if (row) row.remove();
  }
  /* Answers land after a short beat rather than instantly — an instant reply reads as a
     canned page element, which is exactly what a customer discounts. */
  function agentReply(text, delay) {
    showTyping();
    setTimeout(function () { hideTyping(); addMsg('agent', text); }, delay || 550);
  }

  function renderQuick() {
    if (!cfg.quickReplies || !cfg.quickReplies.length) return;
    var wrap = document.createElement('div');
    wrap.className = 'fs-chat-quick';
    cfg.quickReplies.forEach(function (qr) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'fs-chat-quick__btn';
      b.textContent = qr.q;
      b.addEventListener('click', function () {
        wrap.remove();
        addMsg('user', qr.q);
        agentReply(qr.a);
        setTimeout(function () { if (msgs) msgs.scrollTop = msgs.scrollHeight; }, 620);
      });
      wrap.appendChild(b);
    });
    msgs.appendChild(wrap);
    msgs.scrollTop = msgs.scrollHeight;
  }

  function open() {
    isOpen = true;
    win.classList.add('is-open');
    bubble.classList.add('is-open');
    bubble.setAttribute('aria-expanded', 'true');
    if (unread) unread.classList.remove('is-visible');
    dismissNudge(false);
    if (!greeted) {
      greeted = true;
      setTimeout(function () { addMsg('agent', cfg.greeting); renderQuick(); }, 220);
    }
    setTimeout(function () { try { input.focus(); } catch (e) {} }, 260);
  }
  function close() {
    isOpen = false;
    win.classList.remove('is-open');
    bubble.classList.remove('is-open');
    bubble.setAttribute('aria-expanded', 'false');
    try { bubble.focus(); } catch (e) {}
  }
  function toggle() { isOpen ? close() : open(); }

  function dismissNudge(persist) {
    if (!nudge) return;
    nudge.classList.remove('is-visible');
    nudge.setAttribute('inert', '');
    if (persist) { try { localStorage.setItem(NUDGE_KEY, '1'); } catch (e) {} }
  }

  function validEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); }

  /* Message mode delivery. THREE approaches were tried against this store before this one:
       1. fetch POST to /contact -> "400 Missing CAPTCHA token". Shopify enforces reCAPTCHA there
          and the token is attached by Shopify's own script, so no background POST can ever pass.
       2. A real {% form 'contact' %} submitted into a hidden iframe -> the submit event fires and
          is then defaultPrevented, and the iframe never navigates: Shopify only wires its captcha
          up on the CONTACT TEMPLATE, so a contact form injected onto a product page is never
          signed.
       3. What ships: collect the question and the email in the chat, then hand the customer to the
          real contact page with both fields already filled, so the send happens on the one form
          Shopify actually protects. One click for them, nothing to host, and the message cannot
          silently vanish — which is what both background approaches did. */
  function handoffToContact(email, body) {
    var url = '/pages/contact?chat=' + encodeURIComponent(body) +
              (email ? '&chat_email=' + encodeURIComponent(email) : '');
    window.location.href = url;
  }

  function startCapture(prefill) {
    pendingQuestion = prefill || pendingQuestion;
    awaitingEmail = true;
    if (humanBar) humanBar.style.display = 'none';
    agentReply("Happy to get that to the team. What is the best email to reply to?");
  }

  function handleSend() {
    var text = (input.value || '').trim();
    if (!text) return;
    input.value = '';
    addMsg('user', text);

    if (awaitingEmail) {
      if (!validEmail(text)) { agentReply("That does not look like an email address. Could you check it?"); return; }
      capturedEmail = text;
      awaitingEmail = false;
      var body = 'Sent from the website chat.\n\n' + transcript.join('\n');
      agentReply("Perfect. I am opening our contact form with your question and email already filled in. Just add your name and press send.", 500);
      setTimeout(function () { handoffToContact(capturedEmail, body); }, 1900);
      return;
    }

    if (cfg.hasEndpoint) { askAssistant(text); return; }
    startCapture(text);
  }

  /* Assistant mode: same request/response contract as the reference, so a worker written
     against their shape drops straight in. */
  function askAssistant(text) {
    history.push({ role: 'user', content: text });
    input.disabled = true; sendBtn.disabled = true;
    showTyping();
    fetch(cfg.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages: history })
    })
      .then(function (r) { if (!r.ok) throw new Error('endpoint ' + r.status); return r.json(); })
      .then(function (data) {
        hideTyping();
        var reply = (data && data.content) || '';
        if (!reply) throw new Error('empty');
        if (reply.indexOf('HANDOFF_REQUESTED') === 0) { startCapture(text); return; }
        history.push({ role: 'assistant', content: reply });
        addMsg('agent', reply);
      })
      .catch(function () {
        hideTyping();
        addMsg('agent', "I cannot reach our assistant right now, so let me pass this to the team instead.");
        setTimeout(function () { startCapture(text); }, 400);
      })
      .then(function () {
        input.disabled = false; sendBtn.disabled = false;
        try { input.focus(); } catch (e) {}
      });
  }

  bubble.addEventListener('click', toggle);
  var closeBtn = win.querySelector('.fs-chat-close');
  if (closeBtn) closeBtn.addEventListener('click', close);
  sendBtn.addEventListener('click', handleSend);
  input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); handleSend(); } });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && isOpen) close(); });

  var humanBtn = win.querySelector('[data-chat-human]');
  if (humanBtn) humanBtn.addEventListener('click', function () {
    if (!isOpen) open();
    startCapture('');
  });

  if (nudge) {
    var closeNudge = document.getElementById('fs-nudge-close');
    if (closeNudge) closeNudge.addEventListener('click', function (e) { e.stopPropagation(); dismissNudge(true); });
    nudge.addEventListener('click', function () { dismissNudge(true); open(); });
    var seen = false;
    try { seen = localStorage.getItem(NUDGE_KEY) === '1'; } catch (e) {}
    if (!seen) {
      /* The nudge is position:fixed near the bottom of the viewport, so a bare timer fired it
         wherever the reader happened to be. On a product page at scroll-top that put it straight
         on top of .rating-row: measured at 393x852, nudge 745..779 vs rating row 747..803, and
         document.elementFromPoint over the stars returned the nudge, not the stars. That hid
         "4.7 from 1,277 reviews" at the exact moment a buyer is deciding, directly above the price.
         Now it needs the delay AND a real scroll, so it can only appear once the opening screen
         (rating, price, add-to-cart) is behind the reader.
         The gate is capped at whatever the page can actually scroll, so a short page that never
         reaches 75% of a viewport still shows the nudge instead of silently losing it. */
      var nudgeArmed = false, nudgeShown = false;
      var nudgeGate = function () {
        var maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        return Math.min(window.innerHeight * 0.75, maxScroll);
      };
      var maybeShowNudge = function () {
        if (nudgeShown || isOpen || !nudgeArmed) return;
        if (window.scrollY < nudgeGate()) return;
        nudgeShown = true;
        window.removeEventListener('scroll', maybeShowNudge);
        nudge.classList.add('is-visible');
        nudge.removeAttribute('inert');      // only focusable once actually shown
      };
      window.addEventListener('scroll', maybeShowNudge, { passive: true });
      setTimeout(function () { nudgeArmed = true; maybeShowNudge(); }, cfg.nudgeDelay || 12000);
    }
  }

  if (statusEl && !cfg.hasEndpoint && !statusEl.textContent.trim()) {
    statusEl.textContent = 'Ask us anything';
  }
})();

/* SILENT-FAILURE GUARD for the contact form.
   Shopify protects /contact with reCAPTCHA. Its script intercepts the submit, asks Google for a
   token, and only then lets the form through. When that Google resource is BLOCKED — an ad
   blocker, a privacy extension, a VPN, a corporate network — the token never arrives, the submit
   is swallowed and the customer is shown NOTHING. Verified on this store in two different
   browsers with a real click: no navigation, no error, message gone.
   The theme cannot supply the token, but it can refuse to fail silently. If a submit has not
   taken the page away after 8 seconds, say so and hand over a mailto that already carries what
   they wrote, so the message is recovered instead of lost. */
(function () {
  'use strict';
  var form = document.querySelector('form[action*="/contact"]');
  if (!form || !document.body.className.match(/template-page|template-index|template-product/)) return;
  if (window.location.search.indexOf('contact_posted') !== -1) return;

  form.addEventListener('submit', function () {
    if (!form.checkValidity || !form.checkValidity()) return;   // the browser will block it anyway
    setTimeout(function () {
      if (document.getElementById('fs-contact-stuck')) return;
      var bodyEl = form.querySelector('[name="contact[body]"]');
      var mailEl = form.querySelector('[name="contact[email]"]');
      var to = (window.fsChatConfig && window.fsChatConfig.email) || '';
      var text = bodyEl ? bodyEl.value : '';
      var note = document.createElement('div');
      note.id = 'fs-contact-stuck';
      note.className = 'fs-contact-stuck';
      note.setAttribute('role', 'alert');
      var link = '';
      if (to) {
        link = '<a href="mailto:' + to +
               '?subject=' + encodeURIComponent('Message from the website') +
               '&body=' + encodeURIComponent(text) + '">email it to us instead</a>';
      }
      note.innerHTML = '<strong>That would not send.</strong> Your browser is most likely blocking ' +
        'our spam protection. An ad blocker, a privacy extension or a VPN will do it. ' +
        'Turn it off for this site and try again' +
        (link ? ', or ' + link + ' (your message is already in there).' : '.') +
        (mailEl && mailEl.value ? '' : '');
      form.appendChild(note);
      note.scrollIntoView({ block: 'center' });
    }, 8000);
  });
})();

/* Prefill the real contact form when the chat hands off to it (see handoffToContact).
   The send itself has to happen on this page because it is the only one where Shopify
   wires up the captcha that /contact requires. */
(function () {
  'use strict';
  try {
    var qs = new URLSearchParams(window.location.search);
    var chat = qs.get('chat');
    if (!chat) return;
    var form = document.querySelector('form[action*="/contact"]');
    if (!form) return;
    var body = form.querySelector('textarea[name="contact[body]"]');
    var email = form.querySelector('[name="contact[email]"]');
    /* Dispatch input events: the contact section runs its own character counter, and a value
       set straight onto .value leaves it reading "0 / 1000" under a full message box. */
    function setVal(el, v) {
      if (!el || el.value) return;
      el.value = v;
      el.dispatchEvent(new Event('input', { bubbles: true }));
      el.dispatchEvent(new Event('change', { bubbles: true }));
    }
    setVal(body, chat);
    setVal(email, qs.get('chat_email') || '');
    form.scrollIntoView({ block: 'center' });
    /* Focus whatever is still MISSING rather than the send button. The contact form marks
       contact[name] required and the chat never asks for a name, so focusing submit just
       bounced off HTML5 validation with no clue why. */
    setTimeout(function () {
      var next = null;
      var required = form.querySelectorAll('[required]');
      for (var i = 0; i < required.length; i++) {
        if (!String(required[i].value || '').trim()) { next = required[i]; break; }
      }
      try { (next || form.querySelector('[type="submit"]')).focus(); } catch (e) {}
    }, 300);
  } catch (e) { /* prefill is a convenience; the form still works empty */ }
})();
